import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Mail, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { formatDate, formatApiError, inputCls } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyContract = () => ({ label: "", insurer: "", renewal_date: "", note: "" });

export const AdminPasses = ({ token, logout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [form, setForm] = useState({ client_name: "", client_email: "", note: "", contracts: [emptyContract()] });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [copied, setCopied] = useState("");
  const [sentTo, setSentTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const res = await fetch(`${API}/admin/client-pass`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      setListError("Pasy se nepodařilo načíst. Zkontrolujte připojení.");
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    load();
  }, [load]);

  const setContract = (index, key, value) => {
    const contracts = form.contracts.map((c, i) => (i === index ? { ...c, [key]: value } : c));
    setForm({ ...form, contracts });
  };

  const addContract = () => setForm({ ...form, contracts: [...form.contracts, emptyContract()] });
  const removeContract = (index) => setForm({ ...form, contracts: form.contracts.filter((_, i) => i !== index) });

  const onCreate = async (e) => {
    e.preventDefault();
    setFeedback("");
    const next = {};
    if (form.client_name.trim().length < 2) next.client_name = "Jméno klienta je povinné.";
    if (!EMAIL_RE.test(form.client_email.trim())) next.client_email = "Zadejte platný e-mail klienta.";
    setFormErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/client-pass`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: form.client_name.trim(),
          client_email: form.client_email.trim(),
          note: form.note.trim(),
          contracts: form.contracts.filter((c) => c.label.trim().length >= 2),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) return logout();
      if (res.ok) {
        setForm({ client_name: "", client_email: "", note: "", contracts: [emptyContract()] });
        setFeedback("Pas byl vytvořený — teď klientovi můžete poslat odkaz e-mailem.");
        load();
      } else {
        setListError(formatApiError(data.detail));
      }
    } catch {
      setListError("Pas se nepodařilo vytvořit. Zkontrolujte připojení.");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (item) => {
    const url = `${window.location.origin}/pas/${item.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(item.id);
      setTimeout(() => setCopied(""), 2000);
    } catch {
      window.prompt("Zkopírujte odkaz:", url);
    }
  };

  const sendPass = async (item) => {
    setFeedback("");
    try {
      const res = await fetch(`${API}/admin/client-pass/${item.id}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) return logout();
      if (res.ok) {
        setSentTo(item.id);
        setTimeout(() => setSentTo(""), 3000);
      } else {
        const data = await res.json().catch(() => ({}));
        setListError(formatApiError(data.detail));
      }
    } catch {
      setListError("E-mail se nepodařilo odeslat.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Opravdu smazat pas pro ${item.client_name}? Odkaz přestane fungovat.`)) return;
    try {
      const res = await fetch(`${API}/admin/client-pass/${item.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) return logout();
      if (res.ok) setItems(items.filter((i) => i.id !== item.id));
    } catch {
      setListError("Pas se nepodařilo smazat.");
    }
  };

  return (
    <div data-testid="admin-tab-passes">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-2xl md:text-3xl text-[#252525]">Pojišťovací pasy klientů</h2>
        <button
          type="button"
          onClick={load}
          data-testid="pass-refresh-button"
          className="inline-flex items-center gap-2 border border-[#E5E5E5] bg-white hover:border-[#B69A58] transition-colors duration-300 px-5 py-3 text-xs uppercase tracking-widest font-medium text-[#252525]"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          Obnovit
        </button>
      </div>

      <form onSubmit={onCreate} noValidate data-testid="pass-create-form" className="mt-8 border border-[#F0F0F0] bg-white p-6 md:p-8">
        <h3 className="font-display text-xl text-[#252525]">Nový pas</h3>
        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div>
            <label htmlFor="pass-client-name" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2">Jméno klienta *</label>
            <input
              id="pass-client-name"
              data-testid="pass-client-name-input"
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className={inputCls}
            />
            {formErrors.client_name && <p role="alert" data-testid="pass-name-error" className="text-sm text-red-700 mt-2">{formErrors.client_name}</p>}
          </div>
          <div>
            <label htmlFor="pass-client-email" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2">E-mail klienta *</label>
            <input
              id="pass-client-email"
              data-testid="pass-client-email-input"
              type="email"
              value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              className={inputCls}
            />
            {formErrors.client_email && <p role="alert" data-testid="pass-email-error" className="text-sm text-red-700 mt-2">{formErrors.client_email}</p>}
          </div>
        </div>
        <div className="mt-6">
          <label htmlFor="pass-note" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2">
            Poznámka pro klienta <span className="normal-case font-normal">(nepovinné — zobrazí se v pasu)</span>
          </label>
          <input
            id="pass-note"
            data-testid="pass-note-input"
            type="text"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Např. Přehled po schůzce 12. 11."
            className={inputCls}
          />
        </div>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-[#737373] font-medium mb-4">Smlouvy</p>
          <div className="space-y-4">
            {form.contracts.map((c, i) => (
              <div key={i} className="border border-[#F0F0F0] bg-[#FAFAF8] p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end" data-testid={`pass-contract-row-${i}`}>
                <div>
                  <label className="block text-xs text-[#737373] mb-1" htmlFor={`pass-c-label-${i}`}>Název smlouvy *</label>
                  <input
                    id={`pass-c-label-${i}`}
                    data-testid={`pass-contract-label-${i}`}
                    type="text"
                    value={c.label}
                    onChange={(e) => setContract(i, "label", e.target.value)}
                    placeholder="Např. Povinné ručení — Škoda Octavia"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#737373] mb-1" htmlFor={`pass-c-insurer-${i}`}>Pojišťovna / banka</label>
                  <input
                    id={`pass-c-insurer-${i}`}
                    data-testid={`pass-contract-insurer-${i}`}
                    type="text"
                    value={c.insurer}
                    onChange={(e) => setContract(i, "insurer", e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#737373] mb-1" htmlFor={`pass-c-renewal-${i}`}>Výročí smlouvy</label>
                  <input
                    id={`pass-c-renewal-${i}`}
                    data-testid={`pass-contract-renewal-${i}`}
                    type="date"
                    value={c.renewal_date}
                    onChange={(e) => setContract(i, "renewal_date", e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-xs text-[#737373] mb-1" htmlFor={`pass-c-note-${i}`}>Poznámka</label>
                    <input
                      id={`pass-c-note-${i}`}
                      data-testid={`pass-contract-note-${i}`}
                      type="text"
                      value={c.note}
                      onChange={(e) => setContract(i, "note", e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  {form.contracts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContract(i)}
                      data-testid={`pass-contract-remove-${i}`}
                      className="p-3 text-[#737373] hover:text-red-700 transition-colors duration-300"
                      aria-label="Odebrat smlouvu"
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addContract}
            data-testid="pass-add-contract"
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#252525] hover:text-[#B69A58] transition-colors duration-300"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            Přidat smlouvu
          </button>
        </div>

        <button
          type="submit"
          data-testid="pass-create-submit"
          disabled={saving}
          className="mt-8 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-widest font-medium disabled:opacity-50"
        >
          {saving ? "Vytvářím…" : "Vytvořit pas"}
        </button>
        {feedback && <p role="status" data-testid="pass-create-success" className="text-sm text-[#8A6D2F] mt-4">{feedback}</p>}
      </form>

      {listError && <p role="alert" data-testid="pass-list-error" className="text-sm text-red-700 mt-6">{listError}</p>}

      {!loading && items.length === 0 && !listError && (
        <div className="mt-8 border border-[#F0F0F0] bg-white p-10 text-center" data-testid="pass-empty-list">
          <p className="text-base text-[#737373]">Zatím žádné pasy. První vytvoříte formulářem výše.</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8 space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`pass-row-${item.id}`}
              className="border border-[#F0F0F0] bg-white p-6 flex flex-wrap items-center gap-x-8 gap-y-4"
            >
              <div className="min-w-[180px]">
                <p className="font-medium text-[#252525]">{item.client_name}</p>
                <p className="text-sm text-[#4A4A4A]">{item.client_email}</p>
                <p className="text-xs text-[#737373] mt-1">
                  Vytvořeno {formatDate(item.created_at)} · {item.contracts.length} {item.contracts.length === 1 ? "smlouva" : item.contracts.length >= 2 && item.contracts.length <= 4 ? "smlouvy" : "smluv"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => copyLink(item)}
                  data-testid={`pass-copy-${item.id}`}
                  className="inline-flex items-center gap-2 border border-[#E5E5E5] bg-white hover:border-[#B69A58] transition-colors duration-300 px-5 py-2.5 text-xs uppercase tracking-widest font-medium text-[#252525]"
                >
                  {copied === item.id ? (
                    <>
                      <Check className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} />
                      Zkopírováno
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" strokeWidth={1.5} />
                      Zkopírovat odkaz
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => sendPass(item)}
                  data-testid={`pass-send-${item.id}`}
                  className="inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-5 py-2.5 text-xs uppercase tracking-widest font-medium"
                >
                  {sentTo === item.id ? (
                    <>
                      <Check className="h-4 w-4" strokeWidth={1.5} />
                      Odesláno
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" strokeWidth={1.5} />
                      Poslat klientovi
                    </>
                  )}
                </button>
                <a
                  href={`/pas/${item.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pass-open-${item.id}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#737373] hover:text-[#B69A58] transition-colors duration-300 px-2 py-2.5"
                >
                  Náhled
                </a>
                <button
                  type="button"
                  onClick={() => remove(item)}
                  data-testid={`pass-delete-${item.id}`}
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-medium text-[#737373] hover:text-red-700 transition-colors duration-300 px-2 py-2.5"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  Smazat
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
